import * as moment from "moment";
import { AccionAgregada, IntervalView, MarkView } from "../../../interface/interfaces";

export interface MarcaFueraJornada {
    nInterval: number;
    nType_Mark: number;
    hora: string;
    fecha: string;
}

interface MarcaProyectada {
    time: moment.Moment;
    existing: boolean;
    nInterval: number;
    nType_Mark: number;
}

function proyectarMarcas(
    intervals: IntervalView[],
    acciones: AccionAgregada[]
): MarcaProyectada[] {
    const marks: MarcaProyectada[] = [];

    const pushSlot = (intervalNum: number, type: number, mark: MarkView | null) => {
        if (!mark?.dTiempo_Marca) return;
        const deleted = acciones.some(a =>
            a.nInterval === intervalNum && a.nType_Mark === type && a.actionType === 'delete'
        );
        if (deleted) return;
        const edited = acciones.find(a =>
            a.nInterval === intervalNum && a.nType_Mark === type && a.actionType === 'edit'
        );
        marks.push({
            time: moment(edited?.dDate || mark.dTiempo_Marca),
            existing: true,
            nInterval: intervalNum,
            nType_Mark: type,
        });
    };

    for (const inv of intervals) {
        pushSlot(inv.nInterval, 1, inv.inicio);
        pushSlot(inv.nInterval, 2, inv.fin);
    }

    for (const acc of acciones) {
        if (acc.actionType === 'add' && acc.dDate) {
            marks.push({
                time: moment(acc.dDate),
                existing: false,
                nInterval: acc.nInterval,
                nType_Mark: acc.nType_Mark,
            });
        }
    }

    marks.sort((a, b) => a.time.valueOf() - b.time.valueOf());

    const normalized: MarcaProyectada[] = [];
    let previous: moment.Moment | null = null;
    for (const mark of marks) {
        let current = mark.time.clone();
        while (previous && current.isBefore(previous)) {
            current = current.clone().add(1, 'day');
        }
        normalized.push({ ...mark, time: current });
        previous = current;
    }

    return normalized;
}

export function validarInicioEnOtraJornada(
    selectedDate: Date,
    intervals: IntervalView[],
    acciones: AccionAgregada[]
): string {
    const jornada = moment(selectedDate).format('YYYY-MM-DD');
    const normalized = proyectarMarcas(intervals, acciones);

    for (let index = 0; index + 1 < normalized.length; index += 2) {
        const entry = normalized[index];
        if (entry.time.format('YYYY-MM-DD') === jornada) continue;
        let message = `La marca de las <strong>${entry.time.format('HH:mm')}</strong> pertenece al <strong>${entry.time.format('DD/MM/YYYY')}</strong>. Genérala en esa fecha.`;
        const exit = normalized[index + 1];
        if (exit.existing) {
            message += ` La marca de las <strong>${exit.time.format('HH:mm')}</strong> se recomienda eliminar y generar en el día que corresponde.`;
        }
        return message;
    }

    const last = normalized[normalized.length - 1];
    if (last && normalized.length % 2 === 1 && !last.existing && last.time.format('YYYY-MM-DD') !== jornada) {
        return `La marca de las <strong>${last.time.format('HH:mm')}</strong> pertenece al <strong>${last.time.format('DD/MM/YYYY')}</strong>. Genérala en esa fecha.`;
    }

    return '';
}

export function marcasExistentesFueraDeJornada(
    selectedDate: Date,
    intervals: IntervalView[],
    acciones: AccionAgregada[]
): MarcaFueraJornada[] {
    const jornada = moment(selectedDate).format('YYYY-MM-DD');
    const normalized = proyectarMarcas(intervals, acciones);
    const found: MarcaFueraJornada[] = [];

    const push = (mark: MarcaProyectada) => {
        if (!mark.existing) return;
        if (found.some(item => item.nInterval === mark.nInterval && item.nType_Mark === mark.nType_Mark)) return;
        found.push({
            nInterval: mark.nInterval,
            nType_Mark: mark.nType_Mark,
            hora: mark.time.format('HH:mm'),
            fecha: mark.time.format('DD/MM/YYYY'),
        });
    };

    for (let index = 0; index < normalized.length; index += 2) {
        const entry = normalized[index];
        if (entry.time.format('YYYY-MM-DD') === jornada) continue;
        push(entry);
        const exit = normalized[index + 1];
        if (exit) push(exit);
    }

    return found;
}

export function mensajeMarcasFueraDeJornada(marcas: MarcaFueraJornada[]): string {
    if (!marcas.length) return '';
    return marcas
        .map(mark => `La marca de las <strong>${mark.hora}</strong> pertenece al <strong>${mark.fecha}</strong> y ya no corresponde a esta jornada. Elimínala y genérala en esa fecha.`)
        .join('<br>');
}

export function validarRequisitoIntervalos(
    selectedDate: Date,
    intervals: IntervalView[],
    acciones: AccionAgregada[]
): string {
    const isToday = moment(selectedDate).isSame(moment(), 'day');
    if (isToday) return '';

    let effectiveCount = 0;

    for (const inv of intervals) {
        const countSlot = (mark: { dTiempo_Marca: Date } | null, type: number) => {
            if (!mark) return;
            const hasDelete = acciones.some(a => a.nInterval === inv.nInterval && a.nType_Mark === type && a.actionType === 'delete');
            if (!hasDelete) effectiveCount++;
        };
        countSlot(inv.inicio, 1);
        countSlot(inv.fin, 2);
    }

    effectiveCount += acciones.filter(a => a.actionType === 'add').length;

    if (effectiveCount === 0) return '';

    if (effectiveCount % 2 !== 0) {
        return 'Debes registrar marcas completas (Inicio y Fin) para <strong>1 intervalo</strong> este día.';
    }

    return '';
}

export function validarSolapeJornadaVecina(
    hora: moment.Moment,
    intervalosVecinos: IntervalView[]
): string {
    if (!hora?.isValid() || !intervalosVecinos?.length) return '';

    for (const interval of intervalosVecinos) {
        const inicio = interval.inicio?.dTiempo_Marca ? moment(interval.inicio.dTiempo_Marca) : null;
        const fin = interval.fin?.dTiempo_Marca ? moment(interval.fin.dTiempo_Marca) : null;
        if (!inicio?.isValid() || !fin?.isValid() || !fin.isAfter(inicio)) continue;
        if (!hora.isBetween(inicio, fin, 'minute', '[]')) continue;

        const finTexto = fin.isSame(inicio, 'day')
            ? fin.format('HH:mm')
            : `${fin.format('HH:mm')} del ${fin.format('DD/MM/YYYY')}`;
        return `La marca está cubierta por el intervalo del ${inicio.format('DD/MM/YYYY')}, de ${inicio.format('HH:mm')} a ${finTexto}.`;
    }

    return '';
}